import { MktButton } from "./primitives";
import { shortAddr } from "./mkt-format";
import { useT } from "../../i18n";
import type { PrivateSale } from "../../lib/marketplace-api";

/**
 * PrivateSaleCancelDialog — Owner Console private-sale cancellation.
 *
 * When the aligned sale contract is SaleV3_1, confirming triggers a REAL on-chain
 * ``cancelPrivateSale(saleId)``. Success is shown ONLY after the backend confirms
 * a mined receipt — there is NO optimistic / false success. When the deployed
 * contract does not support cancellation (SaleV3), the request fails closed with a
 * typed error and the canonical record is never mutated.
 */
export function PrivateSaleCancelDialog({
  sale,
  submitting,
  succeeded,
  resultError,
  onClose,
  onConfirm,
}: {
  sale: PrivateSale;
  submitting: boolean;
  succeeded: boolean;
  resultError: string | null;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const t = useT();
  const failedClosed = !!resultError;
  const terminal = failedClosed || succeeded;
  return (
    <div
      data-testid="ps-cancel-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
    >
      <div className="w-full max-w-md neon-glass-strong rounded-2xl border border-border-strong p-6 space-y-5">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-lg text-text-primary">{t("marketplace.owner.cancelPsTitle")}</h3>
          <span className="font-mono text-[9.5px] uppercase tracking-[0.2em] text-neon-coral/80">
            {t("marketplace.owner.cancelPsBadge")}
          </span>
        </div>

        <div className="rounded-lg border border-border-divider bg-elevated/50 px-4 py-3 space-y-1">
          <p className="font-mono text-[10px] text-text-muted">
            {t("marketplace.owner.buyer", { addr: shortAddr(sale.buyer) })}
          </p>
          <p className="font-mono text-[10px] text-text-muted">{shortAddr(sale.sale_id)}</p>
        </div>

        {!terminal && (
          <div
            data-testid="ps-cancel-warning"
            className="rounded-lg border border-state-warning/40 bg-state-warning/10 px-4 py-3 font-mono text-[11px] text-state-warning leading-relaxed"
          >
            {t("marketplace.owner.cancelPsWarning")}
          </div>
        )}

        {succeeded && (
          <div
            data-testid="ps-cancel-success"
            className="rounded-lg border border-neon-teal/40 bg-neon-teal/10 px-4 py-3 font-mono text-[11px] text-neon-teal leading-relaxed"
          >
            {t("marketplace.owner.cancelPsSuccess")}
          </div>
        )}

        {failedClosed && (
          <div
            data-testid="ps-cancel-failclosed"
            className="rounded-lg border border-neon-coral/40 bg-neon-coral/10 px-4 py-3 font-mono text-[11px] text-neon-coral leading-relaxed"
          >
            {resultError}
          </div>
        )}

        <div className="flex items-center justify-end gap-3 pt-1">
          <MktButton variant="ghost" testId="ps-cancel-close" onClick={onClose}>
            {t("marketplace.owner.cancelPsClose")}
          </MktButton>
          {!terminal && (
            <MktButton variant="coral" testId="ps-cancel-confirm" disabled={submitting} onClick={onConfirm}>
              {submitting ? t("marketplace.owner.submitting") : t("marketplace.owner.cancelPsConfirm")}
            </MktButton>
          )}
        </div>
      </div>
    </div>
  );
}
